import { BlueprintType } from '../../../../api/blueprints/blueprint.types';
import { ICreateOrderTemplateDTO, IOrderTemplateDetailsDTO, IOrderTemplateValues, IUpdateOrderTemplateDTO } from '../../../../api/blueprints/order.template.types';
import { FormVmValueType } from '../../../../infrastructure/form-viewmodel-controller';
import { convertBoolTypeForMixPanel } from '../../../../tracking-service/tracking-values';
import { ORDER_TEMPLATE_FORM_FIELD_KEYS } from '../../../_events/_manage-event/shared/salesforce-order/order-field-dependencies';
import { booleanValue as readBooleanValue, stringValue as readStringValue } from '../../../_events/_manage-event/shared/salesforce-order/order-form-values';
import { IOrderFormSelectOption } from '../../../_events/_manage-event/shared/salesforce-order/order-form-fields.types';
import { TEMPLATE_DESCRIPTION_KEY, TEMPLATE_NAME_KEY } from './order-template-form.config';

export interface IOrderTemplateAccountNames {
    corporateRelatedName?: string;
    billToLegalEntityName?: string;
    opportunityName?: string;
    channelAgencyReferralName?: string;
}

const stringValue = (values: Record<string, FormVmValueType>, key: string): string =>
    readStringValue(values[key]).trim();

const booleanValue = (values: Record<string, FormVmValueType>, key: string): boolean =>
    readBooleanValue(values[key]);

const withString = (key: keyof IOrderTemplateValues, value: string): Partial<IOrderTemplateValues> =>
    value ? { [key]: value } : {};

export interface IOrderTemplateTrackingContext {
    accountId: number;
    accountName: string;
    templateId: string;
}

export const toOrderTemplateTrackingEvent = (
    values: Record<string, FormVmValueType>,
    context: IOrderTemplateTrackingContext,
    names: IOrderTemplateAccountNames = {},
): Record<string, string | number> => ({
    'Account ID': context.accountId,
    'Account Name': context.accountName,
    'Template ID': context.templateId,
    'Template Name': stringValue(values, TEMPLATE_NAME_KEY) || '',
    'Template Description': stringValue(values, TEMPLATE_DESCRIPTION_KEY) || '',
    'Contract #': stringValue(values, 'contractNumber'),
    'Multi Event Order': stringValue(values, 'multiEventOrder'),
    'Parent Order': stringValue(values, 'parentOrder'),
    'Requester Contact': stringValue(values, 'requesterContact'),
    'Corporate Related': stringValue(values, 'corporateRelated'),
    'Opportunity': names.opportunityName ?? '',
    'Channel/Agency Referral': names.channelAgencyReferralName ?? '',
    'Host LOB': stringValue(values, 'hostLob'),
    'Host Sub LOB': stringValue(values, 'hostSubLob'),
    'Order Type': stringValue(values, 'orderType'),
    'Order Subtype': stringValue(values, 'orderSubtype'),
    'Event Type (OE Defined)': stringValue(values, 'eventType'),
    'Event Type (Client Defined)': stringValue(values, 'eventTypeClient'),
    'Hybrid Event': stringValue(values, 'hybridEvent'),
    'High Profile Order': convertBoolTypeForMixPanel(booleanValue(values, 'highProfileOrder')),
    'High Profile Reasoning': stringValue(values, 'highProfileReasoning'),
    'High Profile Attendee Details Provided': convertBoolTypeForMixPanel(Boolean(stringValue(values, 'highProfileAttendeeDetails'))),
    'Account Legal Entity': stringValue(values, 'accountLegalEntity'),
    'Is This Event Repeatable': stringValue(values, 'isThisEventRepeatable'),
    'No Staffing Required': convertBoolTypeForMixPanel(booleanValue(values, 'noStaffRequired')),
    'Sync to Teams': convertBoolTypeForMixPanel(booleanValue(values, 'syncToTeams')),
    'Bill To (Legal Entity)': stringValue(values, 'billToLegalEntity'),
    'Billing Contact': stringValue(values, 'billingContact'),
    'Additional Recipient 1': stringValue(values, 'additionalRecipient1'),
    'Additional Recipient 2': stringValue(values, 'additionalRecipient2'),
    'Billing Method': stringValue(values, 'billingMethod'),
    'Client Cost Code': stringValue(values, 'clientCostCode'),
    'Secondary PO': stringValue(values, 'secondaryPO'),
    'Billing Time Sensitivity': stringValue(values, 'billingTimeSensitivity'),
});

export const toCreateOrderTemplateDto = (
    accountId: number,
    values: Record<string, FormVmValueType>,
    accountNames: IOrderTemplateAccountNames = {},
): ICreateOrderTemplateDTO => {
    const templateDescription = stringValue(values, TEMPLATE_DESCRIPTION_KEY);
    const orderTemplate: IOrderTemplateValues = {
        ...withString('contractId', stringValue(values, 'contractNumber')),
        ...withString('multiEventOrder', stringValue(values, 'multiEventOrder')),
        ...withString('parentOrder', stringValue(values, 'parentOrder')),
        ...withString('requesterContact', stringValue(values, 'requesterContact')),
        ...withString('corporateRelated', stringValue(values, 'corporateRelated')),
        ...withString('corporateRelatedName', accountNames.corporateRelatedName ?? ''),
        ...withString('opportunityId', stringValue(values, 'opportunity')),
        ...withString('opportunityName', accountNames.opportunityName ?? ''),
        ...withString('channelAgencyReferral', stringValue(values, 'channelAgencyReferral')),
        ...withString('channelAgencyReferralName', accountNames.channelAgencyReferralName ?? ''),
        ...withString('hostLob', stringValue(values, 'hostLob')),
        ...withString('hostSubLob', stringValue(values, 'hostSubLob')),
        ...withString('orderType', stringValue(values, 'orderType')),
        ...withString('orderSubtype', stringValue(values, 'orderSubtype')),
        ...withString('eventType', stringValue(values, 'eventType')),
        ...withString('eventTypeClient', stringValue(values, 'eventTypeClient')),
        ...withString('hybridEvent', stringValue(values, 'hybridEvent')),
        highProfileOrder: booleanValue(values, 'highProfileOrder'),
        ...withString('highProfileReasoning', stringValue(values, 'highProfileReasoning')),
        ...withString('highProfileAttendeeDetails', stringValue(values, 'highProfileAttendeeDetails')),
        ...withString('accountLegalEntity', stringValue(values, 'accountLegalEntity')),
        ...withString('isThisEventRepeatable', stringValue(values, 'isThisEventRepeatable')),
        noStaffRequired: booleanValue(values, 'noStaffRequired'),
        syncToTeams: booleanValue(values, 'syncToTeams'),
        ...withString('billToLegalEntity', stringValue(values, 'billToLegalEntity')),
        ...withString('billToLegalEntityName', accountNames.billToLegalEntityName ?? ''),
        ...withString('billingContact', stringValue(values, 'billingContact')),
        ...withString('additionalRecipient1', stringValue(values, 'additionalRecipient1')),
        ...withString('additionalRecipient2', stringValue(values, 'additionalRecipient2')),
        ...withString('billingMethod', stringValue(values, 'billingMethod')),
        ...withString('clientCostCode', stringValue(values, 'clientCostCode')),
        ...withString('secondaryPO', stringValue(values, 'secondaryPO')),
        ...withString('billingTimeSensitivity', stringValue(values, 'billingTimeSensitivity')),
        ...withString('billingNotes', stringValue(values, 'billingNotes')),
        ...withString('description', stringValue(values, 'description')),
    };

    return {
        accountId: accountId,
        type: BlueprintType.ORDER,
        name: stringValue(values, TEMPLATE_NAME_KEY),
        ...(templateDescription ? { description: templateDescription } : {}),
        orderTemplate: orderTemplate,
    };
};

export const toUpdateOrderTemplateDto = (
    accountId: number,
    blueprintId: string,
    createdAt: string,
    values: Record<string, FormVmValueType>,
    accountNames: IOrderTemplateAccountNames = {},
): IUpdateOrderTemplateDTO => ({
    ...toCreateOrderTemplateDto(accountId, values, accountNames),
    blueprintId: blueprintId,
    createdAt: createdAt,
});

const BOOLEAN_FORM_KEYS = new Set(['highProfileOrder', 'noStaffRequired', 'syncToTeams']);

const TRACKED_FORM_KEYS = [TEMPLATE_NAME_KEY, TEMPLATE_DESCRIPTION_KEY, ...ORDER_TEMPLATE_FORM_FIELD_KEYS];

export const changedOrderTemplateFields = (
    before: Record<string, FormVmValueType>,
    after: Record<string, FormVmValueType>,
): string[] => TRACKED_FORM_KEYS.filter(key => BOOLEAN_FORM_KEYS.has(key)
    ? booleanValue(before, key) !== booleanValue(after, key)
    : stringValue(before, key) !== stringValue(after, key));

export const toEditOrderTemplateTrackingEvent = (
    values: Record<string, FormVmValueType>,
    context: IOrderTemplateTrackingContext,
    names: IOrderTemplateAccountNames = {},
    fieldsChanged: string[] = [],
): Record<string, string | number> => ({
    ...toOrderTemplateTrackingEvent(values, context, names),
    'Fields Changed': fieldsChanged.join(', '),
});

export interface IOrderTemplateFormState {
    values: Record<string, FormVmValueType>;
    corporateRelated: IOrderFormSelectOption | null;
    billToLegalEntity: IOrderFormSelectOption | null;
    channelAgencyReferral: IOrderFormSelectOption | null;
    opportunity: IOrderFormSelectOption | null;
}

const text = (value?: string): string => value ?? '';

const checkboxValue = (value?: boolean): '' | 'Yes' => value === true ? 'Yes' : '';

const lookupOption = (id?: string, name?: string): IOrderFormSelectOption | null =>
    id ? { value: id, label: name || id } : null;

export const toOrderTemplateFormState = (dto: IOrderTemplateDetailsDTO): IOrderTemplateFormState => {
    const order = dto.orderTemplate ?? ({} as IOrderTemplateValues);

    return {
        values: {
            [TEMPLATE_NAME_KEY]: text(dto.name),
            [TEMPLATE_DESCRIPTION_KEY]: text(dto.description),
            contractNumber: text(order.contractId),
            multiEventOrder: text(order.multiEventOrder),
            parentOrder: text(order.parentOrder),
            requesterContact: text(order.requesterContact),
            corporateRelated: text(order.corporateRelated),
            opportunity: text(order.opportunityId),
            channelAgencyReferral: text(order.channelAgencyReferral),
            hostLob: text(order.hostLob),
            hostSubLob: text(order.hostSubLob),
            orderType: text(order.orderType),
            orderSubtype: text(order.orderSubtype),
            eventType: text(order.eventType),
            eventTypeClient: text(order.eventTypeClient),
            hybridEvent: text(order.hybridEvent),
            highProfileOrder: checkboxValue(order.highProfileOrder),
            highProfileReasoning: text(order.highProfileReasoning),
            highProfileAttendeeDetails: text(order.highProfileAttendeeDetails),
            accountLegalEntity: text(order.accountLegalEntity),
            isThisEventRepeatable: text(order.isThisEventRepeatable),
            noStaffRequired: checkboxValue(order.noStaffRequired),
            syncToTeams: checkboxValue(order.syncToTeams),
            billToLegalEntity: text(order.billToLegalEntity),
            billingContact: text(order.billingContact),
            additionalRecipient1: text(order.additionalRecipient1),
            additionalRecipient2: text(order.additionalRecipient2),
            billingMethod: text(order.billingMethod),
            clientCostCode: text(order.clientCostCode),
            secondaryPO: text(order.secondaryPO),
            billingTimeSensitivity: text(order.billingTimeSensitivity),
            billingNotes: text(order.billingNotes),
            description: text(order.description),
        },
        corporateRelated: lookupOption(order.corporateRelated, order.corporateRelatedName),
        billToLegalEntity: lookupOption(order.billToLegalEntity, order.billToLegalEntityName),
        channelAgencyReferral: lookupOption(order.channelAgencyReferral, order.channelAgencyReferralName),
        opportunity: lookupOption(order.opportunityId, order.opportunityName),
    };
};
